const API_BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "/api";
const BUILD_ID =
  (import.meta as { env?: { VITE_APP_VERSION?: string } }).env?.VITE_APP_VERSION
  || document.querySelector('meta[name="tasallot-build"]')?.getAttribute("content")
  || "unknown";

const reported = new Set<string>();
let installed = false;

function storageSummary(): Record<string, string> {
  const keys = [
    "tasallot_dark_mode",
    "tasallot_token",
    "tasallot_country_code",
    "tasallot_admin_theme",
    "tasallot_governor_country_id",
    "tasallot_chunk_reload",
  ];
  const out: Record<string, string> = {};
  for (const key of keys) {
    try {
      const v = localStorage.getItem(key) ?? sessionStorage.getItem(key);
      if (v != null) out[key] = v.length > 80 ? `${v.slice(0, 80)}…` : v;
    } catch {
      out[key] = "blocked";
    }
  }
  return out;
}

function reportClientError(payload: {
  message: string;
  stack?: string;
  type: "error" | "rejection" | "boundary" | "console";
}) {
  const fingerprint = `${payload.type}:${payload.message}`.slice(0, 240);
  if (reported.has(fingerprint)) return;
  reported.add(fingerprint);

  const body = JSON.stringify({
    route: typeof window !== "undefined" ? window.location.pathname + window.location.search : "/",
    message: payload.message,
    stack: payload.stack,
    userAgent: navigator.userAgent,
    buildId: BUILD_ID,
    timestamp: new Date().toISOString(),
    localStorage: storageSummary(),
    type: payload.type,
  });

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API_BASE}/v1/client-errors`, new Blob([body], { type: "application/json" }));
      return;
    }
  } catch {
    /* fall through */
  }

  void fetch(`${API_BASE}/v1/client-errors`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function installClientErrorCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const err = event.error;
    reportClientError({
      type: "error",
      message: err instanceof Error ? err.message : String(event.message ?? "Unknown error"),
      stack: err instanceof Error ? err.stack : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    reportClientError({
      type: "rejection",
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });

  const orig = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    orig(...args);
    if (!import.meta.env.PROD) return;
    const msg = args.map(a => (a instanceof Error ? a.message : String(a))).join(" ");
    if (/React|Maximum update depth|render/i.test(msg)) {
      reportClientError({ type: "console", message: msg.slice(0, 500) });
    }
  };
}

export { reportClientError, BUILD_ID };
